import { type ReactNode, useEffect, useRef, useState } from "react";

/*
 * A tooltip that shows on hover and on keyboard focus, wherever its parent's stylesheet says it is
 * needed: the collapsed rail's icons (`rail.tsx`), and the open conversation's actions while their
 * keycaps are folded into tips (`inbox-v2/thread.tsx`). It is decoration for the eye: the control's
 * own name is what assistive technology reads, so the tip is hidden from it rather than read twice.
 * The browser's `title` tooltip did this job before, on four of the eight rail controls, late and
 * never on focus.
 *
 * It follows WCAG 1.4.13, content on hover or focus. Hoverable: the tip sits inside its trigger,
 * so the pointer can move onto it, across a bridge drawn over the gap, without leaving the hover.
 * Persistent: nothing hides it but the pointer and the focus leaving, or Esc. Dismissible: Esc
 * hides a tip that is showing without moving the pointer or the focus, and that Esc is spent there:
 * its default is prevented, so the page's own keys (`notOurs` in `inbox-v2/use-inbox.ts`) and the
 * shell's pass it over, and it goes no further, so no menu, reply box or selection under it acts on
 * it too. It comes back on the next hover or focus.
 */

/** Whether the tip is on screen now: the stylesheet says where and when, and its own menu wins. */
function showing(tip: HTMLElement, trigger: HTMLElement, hovered: boolean): boolean {
  const style = getComputedStyle(tip);
  // Shown only where the stylesheet shows it: not on an expanded rail or a wide head, and not
  // during the hover's beat. A page without the stylesheet shows no tip, so Esc stays the page's.
  if (style.display === "none" || style.visibility !== "visible") return false;
  // The tip never stands over its own open menu, so Esc is the menu's.
  if (trigger.querySelector('[aria-expanded="true"]')) return false;
  try {
    return hovered || trigger.matches(":has(:focus-visible)");
  } catch {
    return hovered;
  }
}

/** Esc for a tip that is showing, the pointer and the focus that bring it back, as listeners. */
function useDismissal(tip: React.RefObject<HTMLSpanElement | null>) {
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const element = tip.current;
    const trigger = element?.parentElement;
    if (!element || !trigger) return;
    let hovered = false;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !showing(element, trigger, hovered)) return;
      event.preventDefault();
      event.stopPropagation();
      setDismissed(true);
    };
    const enter = () => {
      hovered = true;
    };
    const leave = () => {
      hovered = false;
      if (!trigger.contains(document.activeElement)) setDismissed(false);
    };
    const blur = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (!hovered && !(next instanceof Node && trigger.contains(next))) setDismissed(false);
    };
    // Captured on the document, before anything nearer the focus can act on the same key.
    document.addEventListener("keydown", onKey, true);
    trigger.addEventListener("pointerenter", enter);
    trigger.addEventListener("pointerleave", leave);
    trigger.addEventListener("focusout", blur);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      trigger.removeEventListener("pointerenter", enter);
      trigger.removeEventListener("pointerleave", leave);
      trigger.removeEventListener("focusout", blur);
    };
  }, [tip]);
  return dismissed;
}

export function Tip({
  children,
  className = "lcr-rail-tip",
}: {
  children: ReactNode;
  /** Where the tip is drawn: the rail's by default. */
  className?: string;
}) {
  const tip = useRef<HTMLSpanElement>(null);
  const dismissed = useDismissal(tip);
  return (
    <span
      ref={tip}
      className={className}
      aria-hidden="true"
      data-dismissed={dismissed || undefined}
    >
      {children}
    </span>
  );
}
